import type {SupportedLanguage as MFSupportedLanguage} from '@famly/mf_system_provider';
import makeLocalSetup, {defaultLocale, supportedLanguages, type SupportedLanguages} from 'web-app/shared/locale-setup';
import LocalStorageService from 'web-app/services/local-storage';
import {head, isNonEmpty} from 'web-app/util/non-empty-array';

const savedLocale = LocalStorageService.getFromLocalStorage('locale');

const defaultLocaleFromHostname = (hostname: string) => {
    if (hostname.endsWith('.de')) {
        return 'de_DE';
    } else {
        return defaultLocale;
    }
};

export const getBrowserLanguage = () => {
    // Get supported language(s) from browser
    const languages = window.navigator.languages ?? [window.navigator.language];

    return determineLanguageFromBrowserLanguages(languages);
};

export const determineLanguageFromBrowserLanguages = (
    languages: readonly string[],
    hostname = window.location.hostname,
) => {
    // First, convert the languages to the format we use in our codebase
    const formattedLanguages = languages.map(formatNavigatorLanguage);

    // Search through available languages for one that is supported
    const supportedLanguages = formattedLanguages.filter(isSupportedLanguage);

    // If one ore more are found return the first
    if (isNonEmpty(supportedLanguages)) {
        return head(supportedLanguages);
    }

    // If no supported languages are found in the first "pass",
    // try to convert the language only browser locales to a supported language.
    const supportedLanguagesWithLanguageOnlyFallbacks = formattedLanguages
        .map(convertLanguageOnlyToSupportedLanguage)
        .filter(isSupportedLanguage);

    // If one ore more are found return the first
    if (isNonEmpty(supportedLanguagesWithLanguageOnlyFallbacks)) {
        return head(supportedLanguagesWithLanguageOnlyFallbacks);
    }

    // Otherwise fall back to default
    return defaultLocaleFromHostname(hostname);
};

export const getModernFamlyLanguage = (locale: string): MFSupportedLanguage => {
    switch (locale) {
        case 'da_DK':
            return 'daDK';
        // Swiss dutch is not part of `SupportedLanguages`, se we need to check the string "manually" here
        case 'de_CH':
            return 'deCH';
        case 'de_DE':
            return 'deDE';
        case 'es_ES':
            return 'esES';
        case 'es_US':
            return 'esUS';
        case 'en_GB':
            return 'enGB';
        case 'en_US':
            return 'enUS';
        case 'no_NO':
            return 'nbNO';
        case 'nl_NL':
            return 'nlNL';
        default:
            return 'enUS';
    }
};

export const isSupportedLanguage = (lang: string): lang is SupportedLanguages =>
    supportedLanguages.includes(lang as any);

const formatNavigatorLanguage = (lang: string) => lang.replace('-', '_');

/**
 * Attempts to select the supported language from the navigator language,
 * when the language doesn't specify a country code.
 *
 * @param locale
 * @returns A supported locale - if we recognized the language, otherwise the original locale.
 */
const convertLanguageOnlyToSupportedLanguage = (locale: string) => {
    switch (locale) {
        case 'no':
            return 'no_NO';
        case 'da':
            return 'da_DK';
        case 'en':
            return 'en_GB';
        case 'de':
            return 'de_DE';
        case 'es':
            return 'es_ES';
        case 'nl':
            return 'nl_NL';
        default:
            return locale;
    }
};

export default makeLocalSetup(() => Promise.resolve({locale: getBrowserLanguage()}), savedLocale);
